// React hooks for patient risk factors
import React, { useState, useEffect } from 'react';
import { patientRiskFactorService } from '../services/patientRiskFactorService';

/**
 * Hook to get all patient risk factors with optional filters
 */
const usePatientRiskFactors = (filters = {}) => {
  const [factors, setFactors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const loadFactors = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let data;
      
      if (filters.category) {
        data = await patientRiskFactorService.getFactorsByCategory(filters.category);
      } else if (filters.departmentId) {
        data = await patientRiskFactorService.getFactorsByDepartment(filters.departmentId);
      } else if (filters.conditions) {
        data = await patientRiskFactorService.getFactorsByConditions(filters.conditions);
      } else if (filters.ageRange) {
        data = await patientRiskFactorService.getFactorsByAgeRange(filters.ageRange);
      } else if (filters.highImpact) {
        data = await patientRiskFactorService.getHighImpactFactors();
      } else if (filters.activeOnly === false) {
        data = await patientRiskFactorService.getAllFactors();
      } else {
        // Default to active factors only
        data = await patientRiskFactorService.getActiveFactors();
      }
      
      setFactors(data);
    } catch (err) {
      console.error('Error loading patient risk factors:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadFactors();
  }, [
    filters.category,
    filters.departmentId,
    filters.conditions,
    filters.ageRange,
    filters.highImpact,
    filters.activeOnly
  ]);
  
  const refresh = () => {
    loadFactors();
  };
  
  return { factors, loading, error, refresh };
};

/**
 * Hook to get a single patient risk factor by ID
 */
const usePatientRiskFactor = (factorId) => {
  const [factor, setFactor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const loadFactor = async () => {
    if (!factorId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await patientRiskFactorService.getFactor(factorId);
      setFactor(data);
    } catch (err) {
      console.error('Error loading patient risk factor:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadFactor();
  }, [factorId]);
  
  const refresh = () => {
    loadFactor();
  };
  
  return { factor, loading, error, refresh };
};

/**
 * Hook for patient risk factor mutations
 */
const usePatientRiskFactorMutations = () => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const createFactor = async (factorData, userId) => {
    try {
      setSaving(true);
      setError(null);
      const factorId = await patientRiskFactorService.createFactor(factorData, userId);
      return factorId;
    } catch (err) {
      console.error('Error creating patient risk factor:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };
  
  const updateFactor = async (factorId, updates, userId) => {
    try {
      setSaving(true);
      setError(null);
      await patientRiskFactorService.updateFactor(factorId, updates, userId);
    } catch (err) {
      console.error('Error updating patient risk factor:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };
  
  const deleteFactor = async (factorId) => {
    try {
      setSaving(true);
      setError(null);
      await patientRiskFactorService.deleteFactor(factorId);
    } catch (err) {
      console.error('Error deleting patient risk factor:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };
  
  const toggleActive = async (factorId, userId) => {
    try {
      setSaving(true);
      setError(null);
      await patientRiskFactorService.toggleActive(factorId, userId);
    } catch (err) {
      console.error('Error toggling patient risk factor:', err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };
  
  return {
    createFactor,
    updateFactor,
    deleteFactor,
    toggleActive,
    saving,
    error
  };
};

/**
 * Hook to get patient risk factor statistics
 */
const usePatientRiskFactorStatistics = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await patientRiskFactorService.getFactorStatistics();
      setStatistics(data);
    } catch (err) {
      console.error('Error loading patient risk factor statistics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadStatistics();
  }, []);
  
  const refresh = () => {
    loadStatistics();
  };
  
  return { statistics, loading, error, refresh };
};

/**
 * Hook to calculate total multiplier for a set of factor IDs
 */
const usePatientRiskMultiplier = (factorIds) => {
  const [multiplier, setMultiplier] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const calculateMultiplier = async () => {
    try {
      setLoading(true);
      setError(null);
      const total = await patientRiskFactorService.calculateTotalMultiplier(factorIds);
      setMultiplier(total);
    } catch (err) {
      console.error('Error calculating patient risk multiplier:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (factorIds && factorIds.length > 0) {
      calculateMultiplier();
    } else {
      setMultiplier(1.0);
      setLoading(false);
    }
  }, [JSON.stringify(factorIds)]); // Use JSON.stringify for array comparison
  
  return { multiplier, loading, error };
};

export {
  usePatientRiskFactors,
  usePatientRiskFactor,
  usePatientRiskFactorMutations,
  usePatientRiskFactorStatistics,
  usePatientRiskMultiplier
};

